import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, FileText, AlertTriangle, UserPlus, Search, ChevronRight } from "lucide-react";

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold leading-none">{value}</div>
          <div className="text-sm text-muted-foreground mt-1">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

const empty = { name: "", national_id: "", start_date: "", end_date: "", active: true };

export default function HRHome() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ employees: 0, documents: 0, issues: 0 });
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const size = 25;

  const loadSummary = () => api.get("/hrdoc/summary").then((r) => setSummary(r.data));
  const loadEmployees = () =>
    api.get("/hrdoc/employees", { params: { q, page, size } }).then((r) => {
      setRows(r.data.items);
      setTotal(r.data.total);
    });

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadEmployees(); /* eslint-disable-next-line */ }, [q, page]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/hrdoc/employees", form);
      toast.success("Werknemer toegevoegd");
      setOpen(false);
      setForm(empty);
      loadSummary();
      loadEmployees();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / size));

  return (
    <div className="space-y-8" data-testid="hr-home">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Personeelsdossiers</h1>
          <p className="text-muted-foreground mt-1">Werknemers, documenten en volledigheid per dossier.</p>
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="hr-add-employee-btn" className="bg-teal-600 hover:bg-teal-700">
                <UserPlus className="h-4 w-4 mr-2" /> Nieuwe werknemer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nieuwe werknemer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Naam</Label>
                  <Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Voornaam Achternaam" /></div>
                <div><Label>Rijksregisternummer (optioneel)</Label>
                  <Input data-testid="emp-nid" value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} placeholder="11 cijfers" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>In dienst</Label><Input type="date" data-testid="emp-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div><Label>Uit dienst</Label><Input type="date" data-testid="emp-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button data-testid="emp-save" disabled={saving} onClick={save} className="bg-teal-600 hover:bg-teal-700">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Werknemers" value={summary.employees} tone="bg-teal-50 text-teal-700" />
        <StatCard icon={FileText} label="Documenten" value={summary.documents} tone="bg-blue-50 text-blue-700" />
        <StatCard icon={AlertTriangle} label="Aandachtspunten" value={summary.issues} tone="bg-amber-50 text-amber-700" />
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="relative max-w-sm mb-4">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="hr-emp-search" className="pl-9" placeholder="Zoek op naam of rijksregisternummer"
                   value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Werknemer</TableHead>
                <TableHead>Rijksregisternr.</TableHead>
                <TableHead className="text-right">Documenten</TableHead>
                <TableHead className="text-right">Aandacht</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} data-testid={`hr-emp-row-${e.id}`} className="cursor-pointer hover:bg-accent/50"
                          onClick={() => navigate(`/hr/employee/${e.id}`)}>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell className="text-muted-foreground">{e.national_id || "—"}</TableCell>
                  <TableCell className="text-right">{e.total}</TableCell>
                  <TableCell className="text-right">
                    {e.issues > 0 ? <Badge className="bg-amber-100 text-amber-700 border-amber-200">{e.issues}</Badge> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground inline" /></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Geen werknemers gevonden.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">Pagina {page} / {pages}</span>
              <div className="space-x-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Vorige</Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>Volgende</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
